// The Trade prices tab: edit the trade price list, see the printed A5 sheet
// update beside it, and save it as a PDF (on its own, or two to an A4 sheet
// for printing and cutting). New invoice lines take their prices from here;
// invoices already made keep the prices they were made with.
import { useState } from 'react';
import { api } from '../lib/auth';
import { Button, Card, Input, Textarea } from '../lib/ui';
import { priceListDoc } from '../lib/tradeDocs';
import { savePdf } from '../lib/pdf';
import { newKey } from '../lib/trade';
import { Label, errorText } from './TradeCommon';
import TradeDocPreview from './TradeDocPreview';

const keyOf = (x) => x._id || x._key;
// Saved prices are numbers: show pence when there are any ("5.50", "25").
// While being typed they're strings and shown as typed.
const priceValue = (p) => (typeof p === 'number' ? (Number.isInteger(p) ? String(p) : p.toFixed(2)) : p);
const clone = (v) => JSON.parse(JSON.stringify(v));

function move(list, index, by) {
  const next = list.slice();
  const [item] = next.splice(index, 1);
  next.splice(index + by, 0, item);
  return next;
}

function IconButtons({ index, length, onMove, onRemove, what }) {
  return (
    <div className="pl-icon-btns">
      <button type="button" title={`Move ${what} up`} disabled={index === 0} onClick={() => onMove(-1)}>↑</button>
      <button type="button" title={`Move ${what} down`} disabled={index === length - 1} onClick={() => onMove(1)}>↓</button>
      <button type="button" className="danger" title={`Remove ${what}`} onClick={onRemove}>×</button>
    </div>
  );
}

export default function TradePrices({ saved, draft, setDraft, onSaved, flash }) {
  const pl = draft || saved;
  const dirty = !!draft;
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState('');
  const [overflow, setOverflow] = useState(false);

  const edit = (fn) => {
    const next = clone(pl);
    fn(next);
    setDraft(next);
  };
  const setTop = (k) => (e) => edit(p => { p[k] = e.target.value; });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/trade/price-list', pl);
      onSaved(data);
      setDraft(null);
      flash('Price list saved');
      return data;
    } catch (e) {
      flash(errorText(e), 'bad');
      return null;
    } finally {
      setSaving(false);
    }
  };

  // Prints the saved list, saving any changes first
  const makePdf = async (twoUp) => {
    const list = dirty ? await save() : pl;
    if (!list) return;
    setBusy(twoUp ? 'two' : 'one');
    try {
      const res = await savePdf(priceListDoc(list, { twoUp }), {
        fileName: twoUp ? 'TearDrop-trade-price-list-A4-2up.pdf' : 'TearDrop-trade-price-list.pdf',
        folder: 'Designs',
      });
      if (res.path) flash(`Saved to ${res.path}`);
    } catch (e) {
      flash(errorText(e), 'bad');
    } finally {
      setBusy('');
    }
  };

  const removeSection = (si) => {
    const s = pl.sections[si];
    if (s.items.length && !window.confirm(`Remove ${s.name || 'this section'} and its ${s.items.length} item${s.items.length === 1 ? '' : 's'}?`)) return;
    edit(p => { p.sections.splice(si, 1); });
  };

  return (
    <div>
      <div className={`pl-savebar${dirty ? ' is-dirty' : ''}`}>
        <span className="grow">{dirty ? 'Unsaved changes' : 'All saved. New invoice lines take their prices from this list.'}</span>
        {dirty && <Button variant="muted" onClick={() => setDraft(null)} disabled={saving}>Discard</Button>}
        <Button variant="primary" onClick={save} disabled={!dirty || saving}>{saving ? 'Saving…' : 'Save price list'}</Button>
      </div>

      <div className="pl-editor">
        <div className="pl-editor__form">
          <Card>
            <div className="inv-grid2">
              <div><Label hint="top right of the sheet">Valid from</Label><Input value={pl.effectiveFrom} onChange={setTop('effectiveFrom')} placeholder="From October 2026" /></div>
            </div>
            <div style={{ marginTop: 12 }}>
              <Label>Intro</Label>
              <Textarea rows={3} value={pl.intro} onChange={setTop('intro')} />
            </div>
          </Card>

          {pl.sections.map((section, si) => (
            <Card key={keyOf(section) || si}>
              <div className="pl-section-card__head">
                <div><Label>Section</Label><Input value={section.name} onChange={e => edit(p => { p.sections[si].name = e.target.value; })} /></div>
                <div><Label hint="priced per">Unit</Label><Input value={section.unit} onChange={e => edit(p => { p.sections[si].unit = e.target.value; })} placeholder="kg" /></div>
                <IconButtons
                  index={si}
                  length={pl.sections.length}
                  what="section"
                  onMove={(by) => edit(p => { p.sections = move(p.sections, si, by); })}
                  onRemove={() => removeSection(si)}
                />
              </div>

              {section.items.length > 0 && (
                <div className="pl-item pl-item--head"><span>Item</span><span>Description</span><span>Price</span><span /></div>
              )}
              {section.items.map((item, ii) => (
                <div className="pl-item" key={keyOf(item) || ii}>
                  <Input value={item.name} onChange={e => edit(p => { p.sections[si].items[ii].name = e.target.value; })} aria-label="Item name" />
                  <Input value={item.description} onChange={e => edit(p => { p.sections[si].items[ii].description = e.target.value; })} aria-label="Description" />
                  <div className="pl-price">
                    <Input
                      type="number" min="0" step="0.5"
                      value={priceValue(item.price)}
                      onChange={e => edit(p => { p.sections[si].items[ii].price = e.target.value; })}
                      aria-label={`${item.name} price per ${section.unit}`}
                    />
                  </div>
                  <IconButtons
                    index={ii}
                    length={section.items.length}
                    what="item"
                    onMove={(by) => edit(p => { p.sections[si].items = move(p.sections[si].items, ii, by); })}
                    onRemove={() => edit(p => { p.sections[si].items.splice(ii, 1); })}
                  />
                </div>
              ))}
              <Button size="sm" variant="ghost" style={{ marginTop: 4 }}
                onClick={() => edit(p => { p.sections[si].items.push({ _key: newKey(), name: '', description: '', price: '' }); })}>
                + Add item
              </Button>
            </Card>
          ))}

          <div>
            <Button onClick={() => edit(p => { p.sections.push({ _key: newKey(), name: '', unit: 'kg', items: [] }); })}>+ Add section</Button>
          </div>

          <Card>
            <Label hint="the green box">Ordering notes</Label>
            <div style={{ display: 'grid', gap: 6 }}>
              {pl.terms.map((t, ti) => (
                <div key={ti} className="trade-row" style={{ flexWrap: 'nowrap' }}>
                  <Input value={t} onChange={e => edit(p => { p.terms[ti] = e.target.value; })} />
                  <IconButtons
                    index={ti}
                    length={pl.terms.length}
                    what="note"
                    onMove={(by) => edit(p => { p.terms = move(p.terms, ti, by); })}
                    onRemove={() => edit(p => { p.terms.splice(ti, 1); })}
                  />
                </div>
              ))}
            </div>
            {pl.terms.length < 10 && (
              <Button size="sm" variant="ghost" style={{ marginTop: 8 }} onClick={() => edit(p => { p.terms.push(''); })}>+ Add note</Button>
            )}
          </Card>

          <Card>
            <Label>Contact on the sheet</Label>
            <div className="inv-grid2">
              <Input value={pl.contactName} onChange={setTop('contactName')} placeholder="Name" aria-label="Contact name" />
              <Input value={pl.phone} onChange={setTop('phone')} placeholder="Phone" aria-label="Phone" />
              <Input value={pl.email} onChange={setTop('email')} placeholder="Email" aria-label="Email" />
              <Input value={pl.website} onChange={setTop('website')} placeholder="Website" aria-label="Website" />
            </div>
          </Card>
        </div>

        <div className="pl-editor__preview">
          <TradeDocPreview doc={priceListDoc(pl)} onOverflow={setOverflow} />
          {overflow && (
            <div className="ui-banner ui-banner--warn">
              That's more than fits on one A5 sheet, so the bottom gets cut off. Shorten some descriptions or take something out.
            </div>
          )}
          <div className="trade-row">
            <Button onClick={() => makePdf(false)} disabled={!!busy || saving}>{busy === 'one' ? 'Making PDF…' : 'Save A5 PDF'}</Button>
            <Button onClick={() => makePdf(true)} disabled={!!busy || saving} title="Two copies side by side on A4, to print and cut in half">
              {busy === 'two' ? 'Making PDF…' : 'Save 2-up A4 PDF'}
            </Button>
          </div>
          <div className="trade-form__hint">The 2-up PDF puts two copies on a landscape A4 sheet, to print and cut in half.</div>
        </div>
      </div>
    </div>
  );
}
